import React from "react";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "@/dentlab/src/components/ui/dialog";
import { useAttributesStore } from "@/dentlab/src/store/Attributes/attributes.store";
import { useCentralStore } from "@/dentlab/src/store/Central";
import { hasFullFeatureSet } from "@/dentlab/src/store/Central/selectors";
import { RolesEnum } from "@/lib/types/roles.types";
import { getAppListMenuItems } from "./app-bar-lists/get-app-list-menu-items.function";
import { ORGANIZATION_MENU_ITEMS } from "@/dentlab/src/pages/organization/organization-menu-config";
import {
	Home,
	Settings,
	UserX,
	FileStack,
	FolderTree,
	Package,
	Tags,
	FileEdit,
	Coins,
	Settings2,
	Printer,
	ClipboardList,
	Building2,
	Star,
	Eye,
	EyeOff,
} from "lucide-react";
import { cn } from "@/dentlab/src/lib/utils/cn";

const ORG_SUBPAGE_ITEMS = [
	{ key: "org-uebersicht", title: "Übersicht", icon: <Home size={16} /> },
	{
		key: "org-administration",
		title: "Administration",
		icon: <Settings size={16} />,
	},
	{
		key: "org-leistungskatalog",
		title: "Leistungskatalog",
		icon: <FolderTree size={16} />,
	},
	{
		key: "org-auftragsansichten",
		title: "Auftragsansichten",
		icon: <Settings2 size={16} />,
	},
	{
		key: "org-pdf-gestaltung",
		title: "PDF-Gestaltung",
		icon: <FileEdit size={16} />,
	},
	{
		key: "org-vorlagen-jumbos",
		title: "Vorlagen/Jumbos",
		icon: <FileStack size={16} />,
	},
	{
		key: "org-tarifkataloge",
		title: "Tarifkataloge",
		icon: <Tags size={16} />,
	},
	{ key: "org-artikel", title: "Artikel", icon: <Package size={16} /> },
	{ key: "org-preislisten", title: "Preislisten", icon: <Coins size={16} /> },
	{
		key: "org-inaktive-kunden",
		title: "Inaktive Kunden",
		icon: <UserX size={16} />,
	},
	{
		key: "org-drucker",
		title: "Druckereinstellungen",
		icon: <Printer size={16} />,
	},
	{
		key: "org-druckerauftraege",
		title: "Druckeraufträge",
		icon: <ClipboardList size={16} />,
	},
] as const;

const ORG_SUBPAGE_PATH_OVERRIDES: Record<string, string> = {
	"org-preislisten": "/organisation/administration/preislisten",
	"org-inaktive-kunden": "/organisation/administration/inaktive-kunden",
	"org-drucker": "/organisation/administration/drucker",
	"org-druckerauftraege": "/organisation/administration/druckerauftraege",
};

export const ORG_SUBPAGE_FAVORITE_MAP: Record<
	string,
	{ title: string; path: string; icon: React.ReactNode }
> = {};
ORG_SUBPAGE_ITEMS.forEach((subItem, idx) => {
	const path =
		ORG_SUBPAGE_PATH_OVERRIDES[subItem.key] ??
		ORGANIZATION_MENU_ITEMS[idx]?.path;
	if (path) {
		ORG_SUBPAGE_FAVORITE_MAP[subItem.key] = {
			title: subItem.title,
			path,
			icon: subItem.icon,
		};
	}
});

// Sidebar sections that render as whole lists (not single links). They can only
// be hidden — there is no single path to pin as a favorite — so they get an
// eye toggle but no star, and live in their own "Bereiche" group in the dialog.
const SECTION_ITEMS = [
	{ key: "vendors", title: "Fremdlabore", icon: <Building2 size={16} /> },
] as const;

interface SidebarFavoritesDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

export const SidebarFavoritesDialog: React.FC<SidebarFavoritesDialogProps> = ({
	open,
	onOpenChange,
}) => {
	const role = useCentralStore((state) => state.role ?? 2);
	const subscriptionTier = useCentralStore((state) => state.subscriptionTier);
	const attributes = useAttributesStore((state) => state.attributes);
	const setAttribute = useAttributesStore((state) => state.setAttribute);
	const deleteAttribute = useAttributesStore(
		(state) => state.deleteAttribute
	);

	const isAdmin = role <= RolesEnum.ADMIN;
	const hasFull = hasFullFeatureSet(subscriptionTier);

	const navItems = getAppListMenuItems(role).filter(
		(item) => !item.isGated || hasFull
	);

	const handleOrgFavToggle = (
		key: string,
		isFav: boolean,
		isShown: boolean
	) => {
		if (isFav) {
			deleteAttribute(`sidebar-fav-${key}`, "profile");
		} else {
			if (isShown) {
				deleteAttribute(`sidebar-show-${key}`, "profile");
			}
			setAttribute(`sidebar-fav-${key}`, 1, "profile");
		}
	};

	const handleOrgShowToggle = (
		key: string,
		isFav: boolean,
		isShown: boolean
	) => {
		if (isShown) {
			deleteAttribute(`sidebar-show-${key}`, "profile");
		} else {
			if (isFav) {
				deleteAttribute(`sidebar-fav-${key}`, "profile");
			}
			setAttribute(`sidebar-show-${key}`, 1, "profile");
		}
	};

	const handleFavToggle = (
		key: string,
		isFav: boolean,
		isHidden: boolean
	) => {
		if (isFav) {
			deleteAttribute(`sidebar-fav-${key}`, "profile");
		} else {
			if (isHidden) {
				deleteAttribute(`sidebar-hide-${key}`, "profile");
			}
			setAttribute(`sidebar-fav-${key}`, 1, "profile");
		}
	};

	const handleHideToggle = (
		key: string,
		isFav: boolean,
		isHidden: boolean
	) => {
		if (isHidden) {
			deleteAttribute(`sidebar-hide-${key}`, "profile");
		} else {
			if (isFav) {
				deleteAttribute(`sidebar-fav-${key}`, "profile");
			}
			setAttribute(`sidebar-hide-${key}`, 1, "profile");
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent
				className="sm:max-w-2xl max-h-[85vh] flex flex-col"
				data-testid="sidebar-favorites-dialog"
			>
				<DialogHeader>
					<DialogTitle>Navigation anpassen</DialogTitle>
				</DialogHeader>

				<div className="flex items-center gap-5 text-xs text-gray-500 px-1">
					<div className="flex items-center gap-1.5">
						<Star
							size={14}
							className="text-yellow-500"
							fill="currentColor"
						/>
						<span>Favorit (oben angeheftet)</span>
					</div>
					<div className="flex items-center gap-1.5">
						<Eye size={14} className="text-gray-700" />
						<span>In Navigation sichtbar</span>
					</div>
					<div className="flex items-center gap-1.5">
						<EyeOff size={14} className="text-gray-400" />
						<span>Ausgeblendet</span>
					</div>
				</div>

				<div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-2 overflow-y-auto">
					<div>
					<div className="text-sm font-medium text-gray-700 mb-2">
							Bereiche
						</div>
						<div className="flex flex-col gap-1.5">
							{SECTION_ITEMS.map((item) => {
								const isHidden =
									attributes[`sidebar-hide-${item.key}`] ===
									1;
								return (
									<div
										key={item.key}
										className={cn(
											"flex items-center gap-2.5 px-2 py-1.5 rounded-md transition-colors",
											isHidden && "opacity-50"
										)}
									>
										<div className="w-5 h-5 flex items-center justify-center text-gray-600">
											{item.icon}
										</div>
										<span className="text-sm text-gray-700 flex-1">
											{item.title}
										</span>
										<button
											onClick={() =>
												handleHideToggle(
													item.key,
													false,
													isHidden
												)
											}
											className={cn(
												"p-1 rounded transition-colors",
												isHidden
													? "text-gray-500 hover:bg-stone-100"
													: "text-gray-400 hover:bg-stone-100"
											)}
											aria-label={
												isHidden
													? "Einblenden"
													: "Ausblenden"
											}
											data-testid={`sidebar-hide-toggle-${item.key}`}
										>
											{isHidden ? (
												<EyeOff size={16} />
											) : (
												<Eye size={16} />
											)}
										</button>
									</div>
								);
							})}
						</div>
						<div className="text-sm font-medium text-gray-700 mb-2 mt-4">
							Navigation
						</div>
						<div className="flex flex-col gap-1.5">
							{navItems.map((item) => {
								const isFav =
									attributes[`sidebar-fav-${item.key}`] === 1;
								const isHidden =
									attributes[`sidebar-hide-${item.key}`] ===
									1;
								return (
									<div
										key={item.key}
										className={cn(
											"flex items-center gap-2.5 px-2 py-1.5 rounded-md transition-colors",
											isHidden && "opacity-50"
										)}
									>
										<div className="w-5 h-5 flex items-center justify-center text-gray-600">
											{item.icon}
										</div>
										<span className="text-sm text-gray-700 flex-1">
											{item.title}
										</span>
										<button
											onClick={() =>
												handleFavToggle(
													item.key,
													isFav,
													isHidden
												)
											}
											className={cn(
												"p-1 rounded transition-colors",
												isFav
													? "text-yellow-500 hover:bg-stone-100"
													: "text-gray-400 hover:bg-stone-100"
											)}
											aria-label={
												isFav
													? "Favorit entfernen"
													: "Als Favorit markieren"
											}
											data-testid={`sidebar-fav-toggle-${item.key}`}
										>
											<Star
												size={16}
												fill={
													isFav
														? "currentColor"
														: "none"
												}
											/>
										</button>
										<button
											onClick={() =>
												handleHideToggle(
													item.key,
													isFav,
													isHidden
												)
											}
											className={cn(
												"p-1 rounded transition-colors",
												isHidden
													? "text-gray-500 hover:bg-stone-100"
													: "text-gray-400 hover:bg-stone-100"
											)}
											aria-label={
												isHidden
													? "Einblenden"
													: "Ausblenden"
											}
											data-testid={`sidebar-hide-toggle-${item.key}`}
										>
											{isHidden ? (
												<EyeOff size={16} />
											) : (
												<Eye size={16} />
											)}
										</button>
									</div>
								);
							})}
						</div>
					</div>

					{isAdmin && (
						<div>
							<div className="text-sm font-medium text-gray-700 mb-2">
								Mein Labor
							</div>
							<div className="flex flex-col gap-1.5">
								{ORG_SUBPAGE_ITEMS.map((item) => {
									const isFav =
										attributes[
											`sidebar-fav-${item.key}`
										] === 1;
									const isShown =
										attributes[
											`sidebar-show-${item.key}`
										] === 1;
									return (
										<div
											key={item.key}
											className="flex items-center gap-2.5 px-2 py-1.5 rounded-md transition-colors"
										>
											<div className="w-5 h-5 flex items-center justify-center text-gray-600">
												{item.icon}
											</div>
											<span className="text-sm text-gray-700 flex-1">
												{item.title}
											</span>
											<button
												onClick={() =>
													handleOrgFavToggle(
														item.key,
														isFav,
														isShown
													)
												}
												className={cn(
													"p-1 rounded transition-colors",
													isFav
														? "text-yellow-500 hover:bg-stone-100"
														: "text-gray-400 hover:bg-stone-100"
												)}
												aria-label={
													isFav
														? "Favorit entfernen"
														: "Als Favorit markieren"
												}
												data-testid={`sidebar-fav-toggle-${item.key}`}
											>
												<Star
													size={16}
													fill={
														isFav
															? "currentColor"
															: "none"
													}
												/>
											</button>
											<button
												onClick={() =>
													handleOrgShowToggle(
														item.key,
														isFav,
														isShown
													)
												}
												className={cn(
													"p-1 rounded transition-colors",
													isFav
														? "text-gray-300 cursor-not-allowed"
														: isShown
															? "text-gray-700 hover:bg-stone-100"
															: "text-gray-400 hover:bg-stone-100"
												)}
												disabled={isFav}
												aria-label={
													isShown || isFav
														? "Aus Navigation entfernen"
														: "In Navigation anzeigen"
												}
												data-testid={`sidebar-show-toggle-${item.key}`}
											>
												{isShown || isFav ? (
													<Eye size={16} />
												) : (
													<EyeOff size={16} />
												)}
											</button>
										</div>
									);
								})}
							</div>
						</div>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
};
